import { Box, Typography } from "@mui/material";
import { RailCarLayoutArea } from "src/store/api/generatedApi";
import {
  AREA_ITEM_SIZES,
  AreaColorMap,
  AreaHotkeyMap,
  AreaMaterialsMap,
  BaseAreaItemProps,
  KeyboardKey,
  UNMAPPED_AREA_COLOR,
} from "./scrapEyeLayoutCommon";
import { LandmarkItem } from "./LandmarkItem";

type RailcarItemProps = BaseAreaItemProps & {
  size?: "normal" | "small";
};

export const RailcarItem = ({
  areaId,
  displayName,
  hotkey,
  material,
  onClick,
  isSelectable = true,
  size = "normal",
  overrideColor,
  isSelected = false,
  isDimmed = false,
  isAwaitingMaterial = false,
}: RailcarItemProps) => {
  const color = overrideColor ?? UNMAPPED_AREA_COLOR;
  const isSmall = size === "small";
  const isPulsing = isAwaitingMaterial && !isDimmed;
  const { width, height } = AREA_ITEM_SIZES.railcar[size];

  return (
    <Box
      onClick={() => isSelectable && !isDimmed && onClick?.(areaId)}
      sx={{
        width,
        height,
        bgcolor: color,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "4px",
        border: isSelected ? "3px solid" : "2px solid",
        borderColor: isSelected ? "primary.main" : "rgba(0,0,0,0.3)",
        cursor: isSelectable && onClick && !isDimmed ? "pointer" : "default",
        transition: "all 0.15s ease-in-out",
        position: "relative",
        gap: 0.25,
        overflow: "hidden",
        opacity: isDimmed ? 0.35 : 1,
        filter: isDimmed ? "grayscale(0.7)" : undefined,
        boxShadow: isSelected
          ? "0 0 0 3px rgba(25, 118, 210, 0.4), 0 4px 12px rgba(0,0,0,0.3)"
          : undefined,
        "&:hover":
          isSelectable && onClick && !isDimmed
            ? {
                transform: "scale(1.05)",
                boxShadow: 4,
                borderColor: "primary.main",
              }
            : {},
        ...(isPulsing && {
          animation: "pulse 1.5s ease-in-out infinite",
          "@keyframes pulse": {
            "0%, 100%": { opacity: 1 },
            "50%": { opacity: 0.45 },
          },
        }),
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: "white",
          fontWeight: "bold",
          textShadow: "1px 1px 2px rgba(0,0,0,0.5)",
          fontSize: isSmall ? "0.55rem" : "0.6rem",
          lineHeight: 1.1,
          textAlign: "center",
          px: 0.25,
        }}
      >
        {displayName}
      </Typography>
      {hotkey ? <KeyboardKey hotkey={hotkey} /> : null}
      {material ? (
        <Typography
          variant="caption"
          sx={{
            color: "white",
            textShadow: "1px 1px 2px rgba(0,0,0,0.5)",
            fontSize: isSmall ? "0.5rem" : "0.55rem",
            lineHeight: 1,
            textAlign: "center",
            px: 0.25,
          }}
        >
          {material}
        </Typography>
      ) : null}
    </Box>
  );
};

type SquareItemProps = BaseAreaItemProps & {
  size?: "normal" | "small";
  width?: number;
  height?: number;
};

export const SquareItem = ({
  areaId,
  displayName,
  onClick,
  isSelectable = true,
  hotkey,
  material,
  size = "normal",
  overrideColor,
  useNeutralFallback = false,
  isSelected = false,
  isDimmed = false,
  width,
  height,
}: SquareItemProps) => {
  const isSmall = size === "small";
  const fallbackColor = useNeutralFallback ? UNMAPPED_AREA_COLOR : "grey.300";
  const bgColor = overrideColor ?? fallbackColor;
  const fallbackSize = AREA_ITEM_SIZES.square[size];

  return (
    <Box
      onClick={() => isSelectable && !isDimmed && onClick?.(areaId)}
      sx={{
        width: width ?? fallbackSize.width,
        height: height ?? fallbackSize.height,
        bgcolor: bgColor,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "4px",
        border: isSelected ? "3px solid" : "2px solid",
        borderColor: isSelected ? "primary.main" : "grey.500",
        cursor: isSelectable && onClick && !isDimmed ? "pointer" : "default",
        transition: "all 0.15s ease-in-out",
        overflow: "hidden",
        opacity: isDimmed ? 0.35 : 1,
        filter: isDimmed ? "grayscale(0.7)" : undefined,
        boxShadow: isSelected
          ? "0 0 0 3px rgba(25, 118, 210, 0.4), 0 4px 12px rgba(0,0,0,0.3)"
          : undefined,
        "&:hover":
          isSelectable && onClick && !isDimmed
            ? {
                transform: "scale(1.05)",
                boxShadow: 4,
                borderColor: "primary.main",
              }
            : {},
      }}
    >
      <Typography
        variant={isSmall ? "body2" : "body1"}
        sx={{
          color:
            overrideColor !== undefined || useNeutralFallback
              ? "white"
              : "grey.800",
          fontWeight: "bold",
          textShadow:
            overrideColor !== undefined || useNeutralFallback
              ? "1px 1px 2px rgba(0,0,0,0.5)"
              : "none",
        }}
      >
        {displayName}
      </Typography>
      {hotkey ? (
        <Box
          sx={{
            mt: 0.5,
            px: 1,
            py: 0.25,
            bgcolor: "rgba(0,0,0,0.5)",
            borderRadius: "4px",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "white",
              fontWeight: "bold",
              fontFamily: "monospace",
              fontSize: isSmall ? "0.75rem" : "0.85rem",
            }}
          >
            {hotkey}
          </Typography>
        </Box>
      ) : null}
      {material ? (
        <Typography
          variant="caption"
          sx={{
            color:
              overrideColor !== undefined || useNeutralFallback
                ? "white"
                : "grey.800",
            textShadow:
              overrideColor !== undefined || useNeutralFallback
                ? "1px 1px 2px rgba(0,0,0,0.5)"
                : "none",
            fontSize: isSmall ? "0.6rem" : "0.7rem",
            lineHeight: 1,
          }}
        >
          {material}
        </Typography>
      ) : null}
    </Box>
  );
};

type CircleItemProps = BaseAreaItemProps & {
  size?: "normal" | "small";
};

export const CircleItem = ({
  areaId,
  displayName,
  onClick,
  isSelectable = true,
  hotkey,
  material,
  size = "normal",
  overrideColor,
  isSelected = false,
  isDimmed = false,
}: CircleItemProps) => {
  const isSmall = size === "small";
  const baseSize = AREA_ITEM_SIZES.circle[size].width;
  const bgColor = overrideColor ?? UNMAPPED_AREA_COLOR;

  return (
    <Box
      onClick={() => isSelectable && !isDimmed && onClick?.(areaId)}
      sx={{
        width: baseSize,
        height: baseSize,
        bgcolor: bgColor,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "50%",
        border: isSelected ? "3px solid" : "2px solid",
        borderColor: isSelected ? "primary.main" : "grey.600",
        cursor: isSelectable && onClick && !isDimmed ? "pointer" : "default",
        transition: "all 0.15s ease-in-out",
        gap: 0.25,
        overflow: "hidden",
        opacity: isDimmed ? 0.35 : 1,
        filter: isDimmed ? "grayscale(0.7)" : undefined,
        boxShadow: isSelected
          ? "0 0 0 3px rgba(25, 118, 210, 0.4), 0 4px 12px rgba(0,0,0,0.3)"
          : undefined,
        "&:hover":
          isSelectable && onClick && !isDimmed
            ? {
                transform: "scale(1.05)",
                boxShadow: 4,
                borderColor: "primary.main",
              }
            : {},
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: "white",
          fontWeight: "bold",
          textShadow: "1px 1px 2px rgba(0,0,0,0.5)",
          fontSize: isSmall ? "0.55rem" : "0.6rem",
          textAlign: "center",
          lineHeight: 1.2,
          px: 0.5,
        }}
      >
        {displayName}
      </Typography>
      {hotkey ? <KeyboardKey hotkey={hotkey} /> : null}
      {material ? (
        <Typography
          variant="caption"
          sx={{
            color: "white",
            textShadow: "1px 1px 2px rgba(0,0,0,0.5)",
            fontSize: isSmall ? "0.5rem" : "0.55rem",
            lineHeight: 1,
            textAlign: "center",
            px: 0.5,
          }}
        >
          {material}
        </Typography>
      ) : null}
    </Box>
  );
};

/**
 * Renders a single area item based on its shape type.
 */
export const LayoutAreaItem = ({
  area,
  onSelectArea,
  nonSelectableAreas,
  areaHotkeys,
  areaColors,
  areaMaterials,
  selectedArea,
  size = "small",
  isLoadingAreaMaterials = false,
}: {
  area: RailCarLayoutArea;
  onSelectArea?: (area: string) => void;
  nonSelectableAreas: readonly string[];
  areaHotkeys?: AreaHotkeyMap;
  areaColors?: AreaColorMap;
  areaMaterials?: AreaMaterialsMap;
  selectedArea?: string | null;
  size?: "normal" | "small";
  isLoadingAreaMaterials?: boolean;
}) => {
  const isSelectable = !nonSelectableAreas.includes(area.area_id);
  const hotkey = areaHotkeys?.[area.area_id];
  const material = areaMaterials?.[area.area_id];
  const overrideColor = areaColors?.[area.area_id];
  const isSelected = selectedArea === area.area_id;
  const isDimmed = selectedArea != null && selectedArea !== area.area_id;

  if (area.shape === "landmark") {
    return (
      <LandmarkItem
        label={area.display_name}
        width={area.width ?? undefined}
        height={area.height ?? undefined}
      />
    );
  }

  if (area.shape === "railcar") {
    return (
      <RailcarItem
        areaId={area.area_id}
        displayName={area.display_name}
        onClick={onSelectArea}
        isSelectable={isSelectable}
        hotkey={hotkey}
        material={material}
        overrideColor={overrideColor}
        isSelected={isSelected}
        isDimmed={isDimmed}
        size={size}
        isAwaitingMaterial={
          isLoadingAreaMaterials && overrideColor === undefined
        }
      />
    );
  }

  if (area.shape === "square") {
    return (
      <SquareItem
        areaId={area.area_id}
        displayName={area.display_name}
        onClick={onSelectArea}
        isSelectable={isSelectable}
        hotkey={hotkey}
        material={material}
        overrideColor={overrideColor}
        isSelected={isSelected}
        isDimmed={isDimmed}
        size={size}
        width={area.width ?? undefined}
        height={area.height ?? undefined}
      />
    );
  }

  // circle shape
  return (
    <CircleItem
      areaId={area.area_id}
      displayName={area.display_name}
      onClick={onSelectArea}
      isSelectable={isSelectable}
      hotkey={hotkey}
      material={material}
      overrideColor={overrideColor}
      isSelected={isSelected}
      isDimmed={isDimmed}
      size={size}
    />
  );
};
